import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, PencilIcon, TagIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import CategoryService from '../../services/CategoryService';

const TagsView = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('6077D1');
  const [tagProcessing, setTagProcessing] = useState(false);
  const [tagParentCategory, setTagParentCategory] = useState(null);
  const [tagMode, setTagMode] = useState('create'); // 'create' | 'edit'
  const [selectedTag, setSelectedTag] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [showDeleteTagModal, setShowDeleteTagModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [deletingTag, setDeletingTag] = useState(false);

  const colorOptions = [
    '6077D1',
    '499962',
    'F97316',
    'E11D48',
    'A855F7',
    '0EA5E9',
    '10B981',
    'FACC15',
    '7C3AED',
    'EF4444'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await CategoryService.getCategories();
      if (result.success) {
        setCategories(result.categories || []);
      } else {
        setError(result.error || 'No se pudieron cargar las etiquetas');
        toast.error(result.error || 'Error al cargar las etiquetas');
      }
    } catch (err) {
      setError(err.message || 'Error al cargar las etiquetas');
      toast.error('Error al cargar las etiquetas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setFormName('');
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (category) => {
    setSelectedCategory(category);
    setFormName(category.name || '');
    setShowEditModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowTagModal(false);
    setShowDeleteModal(false);
    setShowDeleteTagModal(false);
    setFormName('');
    setProcessing(false);
    setSelectedCategory(null);
    setTagName('');
    setTagColor(colorOptions[0]);
    setTagProcessing(false);
    setTagParentCategory(null);
    setTagMode('create');
    setSelectedTag(null);
    setCategoryToDelete(null);
    setDeletingCategory(false);
    setTagToDelete(null);
    setDeletingTag(false);
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setProcessing(true);
    try {
      const result = await CategoryService.createCategory(formName.trim());
      if (result.success) {
        toast.success('Grupo creado correctamente');
        handleCloseModals();
        fetchCategories();
      } else {
        toast.error(result.error || 'No se pudo crear el grupo');
      }
    } catch (error) {
      toast.error('Error al crear el grupo');
      console.error('Error creando categoría:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setProcessing(true);
    try {
      const result = await CategoryService.updateCategory(selectedCategory.id, formName.trim());
      if (result.success) {
        toast.success('Grupo actualizado correctamente');
        handleCloseModals();
        fetchCategories();
      } else {
        toast.error(result.error || 'No se pudo actualizar el grupo');
      }
    } catch (error) {
      toast.error('Error al actualizar el grupo');
      console.error('Error actualizando categoría:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenTagModal = (category) => {
    setTagParentCategory(category);
    setTagName('');
    setTagColor(colorOptions[0]);
    setShowTagModal(true);
    setTagMode('create');
    setSelectedTag(null);
  };

  const handleOpenDeleteModal = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    setDeletingCategory(true);
    try {
      const result = await CategoryService.deleteCategory(categoryToDelete.id);
      if (result.success) {
        toast.success(`Grupo "${categoryToDelete.name}" eliminado`);
        handleCloseModals();
        fetchCategories();
      } else {
        toast.error(result.error || 'No se pudo eliminar el grupo');
      }
    } catch (error) {
      toast.error('Error al eliminar el grupo');
      console.error('Error eliminando categoría:', error);
    } finally {
      setDeletingCategory(false);
    }
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;
    setDeletingTag(true);
    try {
      const result = await CategoryService.deleteTag(tagToDelete.id);
      if (result.success) {
        toast.success('Etiqueta eliminada');
        handleCloseModals();
        fetchCategories();
      } else {
        toast.error(result.error || 'No se pudo eliminar la etiqueta');
      }
    } catch (error) {
      toast.error('Error al eliminar la etiqueta');
      console.error('Error eliminando etiqueta:', error);
    } finally {
      setDeletingTag(false);
    }
  };

  const handleOpenEditTagModal = (category, tag) => {
    setTagParentCategory(category);
    setTagName(tag.name || '');
    setTagColor(tag.color || colorOptions[0]);
    setSelectedTag(tag);
    setTagMode('edit');
    setShowTagModal(true);
  };

  const handleOpenDeleteTagModal = (category, tag) => {
    setTagParentCategory(category);
    setTagToDelete(tag);
    setShowDeleteTagModal(true);
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();

    if (!tagName.trim()) {
      toast.error('El nombre de la etiqueta es obligatorio');
      return;
    }

    if (!tagParentCategory) {
      toast.error('Selecciona un grupo válido');
      return;
    }

    setTagProcessing(true);
    try {
      let result;

      if (tagMode === 'edit' && selectedTag) {
        result = await CategoryService.updateTag(selectedTag.id, {
          name: tagName.trim(),
          categoryId: tagParentCategory.id,
          color: tagColor
        });
      } else {
        result = await CategoryService.createTag({
          name: tagName.trim(),
          categoryId: tagParentCategory.id,
          color: tagColor
        });
      }

      if (result.success) {
        toast.success(tagMode === 'edit' ? 'Etiqueta actualizada correctamente' : 'Etiqueta creada correctamente');
        handleCloseModals();
        fetchCategories();
      } else {
        toast.error(result.error || (tagMode === 'edit' ? 'No se pudo actualizar la etiqueta' : 'No se pudo crear la etiqueta'));
      }
    } catch (error) {
      toast.error(tagMode === 'edit' ? 'Error al actualizar la etiqueta' : 'Error al crear la etiqueta');
      console.error('Error guardando etiqueta:', error);
    } finally {
      setTagProcessing(false);
    }
  };

  const renderTag = (tag) => {
    const color = tag.color?.startsWith('#') ? tag.color : `#${tag.color || '4B5563'}`;
    return (
      <span
        key={tag.id}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
        style={{
          backgroundColor: `${color}20`,
          color
        }}
      >
        {tag.name}
      </span>
    );
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Etiquetas</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Gestiona los grupos de etiquetas y sus valores asociados
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchCategories}
              className="btn-secondary"
              disabled={loading}
            >
              {loading ? 'Actualizando...' : 'Actualizar'}
            </button>
            <button
              onClick={handleOpenCreateModal}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nuevo Grupo</span>
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={fetchCategories}
                className="btn-primary mt-4"
              >
                Reintentar
              </button>
            </div>
          ) : categories.length === 0 ? (
            <div className="p-6 text-center">
              <TagIcon className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No hay grupos de etiquetas
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Comienza creando el primer grupo para organizar tus etiquetas
              </p>
              <button
                onClick={handleOpenCreateModal}
                className="btn-primary"
              >
                Crear Grupo
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Grupo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Etiquetas
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {categories.map((category, index) => (
                    <motion.tr
                      key={category.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {category.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {category.name}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {category.tags && category.tags.length > 0 ? (
                            category.tags.map((tag) => (
                              <div key={tag.id} className="inline-flex items-center space-x-1">
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditTagModal(category, tag)}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors"
                                  style={{
                                    backgroundColor: `${(tag.color?.startsWith('#') ? tag.color : `#${tag.color}`) || '#4B5563'}20`,
                                    color: tag.color?.startsWith('#') ? tag.color : `#${tag.color || '4B5563'}`
                                  }}
                                >
                                  {tag.name}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleOpenDeleteTagModal(category, tag)}
                                  className="p-1 rounded-full text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                                  title="Eliminar etiqueta"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </div>
                            ))
                          ) : (
                            <span className="text-xs text-gray-500">Sin etiquetas</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button
                          onClick={() => handleOpenTagModal(category)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 border border-green-200 dark:border-green-700 rounded-lg transition-colors"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Nueva etiqueta
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(category)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-lg transition-colors"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Editar grupo
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(category)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-700 rounded-lg transition-colors"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Eliminar
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Crear */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={handleCloseModals}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Nuevo Grupo de Etiquetas
                    </h3>
                    <button
                      onClick={handleCloseModals}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre del grupo
                      </label>
                      <input
                        type="text"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        className="input-field"
                        placeholder="Ej: Equipos, Prioridades..."
                        required
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleCloseModals}
                        className="btn-secondary"
                        disabled={processing}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={processing}
                      >
                        {processing ? 'Creando...' : 'Crear'}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Editar */}
      <AnimatePresence>
        {showEditModal && selectedCategory && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={handleCloseModals}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Editar Grupo
                    </h3>
                    <button
                      onClick={handleCloseModals}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                  <form onSubmit={handleUpdateCategory} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre del grupo
                      </label>
                      <input
                        type="text"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        className="input-field"
                        placeholder="Ej: Equipos, Prioridades..."
                        required
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleCloseModals}
                        className="btn-secondary"
                        disabled={processing}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={processing}
                      >
                        {processing ? 'Guardando...' : 'Guardar Cambios'}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Nueva Etiqueta */}
      <AnimatePresence>
        {showTagModal && tagParentCategory && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={handleCloseModals}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {tagMode === 'edit' ? 'Editar etiqueta' : 'Nueva etiqueta'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Grupo: {tagParentCategory.name}
                      </p>
                    </div>
                    <button
                      onClick={handleCloseModals}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateTag} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre de la etiqueta
                      </label>
                      <input
                        type="text"
                        value={tagName}
                        onChange={(e) => setTagName(e.target.value)}
                        className="input-field"
                        placeholder="Ej: QA, Alta, Sprint 1..."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Color
                      </label>
                      <div className="grid grid-cols-5 gap-2">
                        {colorOptions.map((color) => (
                          <button
                            type="button"
                            key={color}
                            className={`w-full h-10 rounded-lg border-2 transition-all ${
                              tagColor === color
                                ? 'border-gray-900 dark:border-white scale-105'
                                : 'border-transparent opacity-80 hover:opacity-100'
                            }`}
                            style={{ backgroundColor: `#${color}` }}
                            onClick={() => setTagColor(color)}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleCloseModals}
                        className="btn-secondary"
                        disabled={tagProcessing}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={tagProcessing}
                      >
                        {tagProcessing ? 'Guardando...' : tagMode === 'edit' ? 'Guardar cambios' : 'Crear etiqueta'}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Eliminar */}
      <AnimatePresence>
        {showDeleteModal && categoryToDelete && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={handleCloseModals}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                      <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Eliminar grupo
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {categoryToDelete.name}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                    ¿Estás seguro de que deseas eliminar este grupo de etiquetas? Esta acción eliminará
                    todas las etiquetas asociadas y no se puede deshacer.
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleCloseModals}
                      className="btn-secondary"
                      disabled={deletingCategory}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteCategory}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400 transition-colors disabled:opacity-60"
                      disabled={deletingCategory}
                    >
                      {deletingCategory ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Eliminar Etiqueta */}
      <AnimatePresence>
        {showDeleteTagModal && tagToDelete && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={handleCloseModals}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                      <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Eliminar etiqueta
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {tagToDelete.name}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                    ¿Deseas eliminar esta etiqueta? Esta acción no se puede deshacer.
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleCloseModals}
                      className="btn-secondary"
                      disabled={deletingTag}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteTag}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400 transition-colors disabled:opacity-60"
                      disabled={deletingTag}
                    >
                      {deletingTag ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TagsView;

